import type { HttpErrorResponse } from '@angular/common/http';
import type { BankingBankAccount } from '@wizbii/utils/models';
import type { Observable } from 'rxjs';

import { inject } from '@angular/core';
import Bugsnag from '@bugsnag/js';
import { environment } from '@environment';
import { AbTestCampaignNameEnum, abTestDefaultVariantMapping } from '@models/ab-test-models';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { initABTestCampaigns, initABTestCampaignsSuccess } from '@stores/abtest/abtest-ngrx.action';
import { bankAccount } from '@stores/banking/banking-ngrx.selector';
import { userId } from '@wizbii-utils/angular/stores';
import { AbTestWebservice, FibiiWebservice } from '@wizbii-utils/angular/webservices';
import { UserTypeEnum } from '@wizbii/utils/models';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';

export const initAbTest = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      map(() => initABTestCampaigns())
    ),
  { functional: true }
);

export const initABTestCampaignsEffect = createEffect(
  (
    actions$ = inject(Actions),
    abTestWebservice = inject(AbTestWebservice),
    fibiiWebservice = inject(FibiiWebservice),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(initABTestCampaigns),
      switchMap(() => {
        return combineLatest([store.select(userId), store.select(bankAccount)]);
      }),
      filter(([userId, bankAccount]) => !!userId && bankAccount !== undefined),
      switchMap(([userId, bankAccount]) => {
        const newJourneyCampaign = getAbTestCampaignByName(AbTestCampaignNameEnum.NewUserJourney);

        return combineLatest([
          of(userId),
          of(bankAccount),
          newJourneyCampaign?.campaignId
            ? abTestWebservice.hasVariantV2(newJourneyCampaign.campaignId, UserTypeEnum.Member, userId!).pipe(
                map((response) => response.hasVariant),
                catchError(() => of(null))
              )
            : of(null),
          fibiiWebservice.getDossiers().pipe(
            map((dossiers) => dossiers.length > 0),
            catchError(() => of(false))
          ),
        ]);
      }),
      switchMap(([userId, bankAccount, hasNewUserJourneyVariant, hasAtLeastOneDossier]) =>
        getNewUserJourneyVariant(
          abTestWebservice,
          userId!,
          !!hasNewUserJourneyVariant,
          bankAccount,
          hasAtLeastOneDossier
        )
      ),
      map((userExperienceAbTestVariant) =>
        initABTestCampaignsSuccess({
          abtestValues: {
            [AbTestCampaignNameEnum.NewUserJourney]: userExperienceAbTestVariant,
          },
        })
      )
    ),
  { functional: true }
);

const getNewUserJourneyVariant = (
  abTestWebservice: AbTestWebservice,
  userId: string,
  hasNewUserJourneyVariant: boolean,
  bankAccount?: BankingBankAccount | null,
  hasAtLeastOneDossier: boolean = false
): Observable<{ value: string }> => {
  const hasIban = !!bankAccount?.currentBankAccount?.iban;
  const isNewUserJourneyDenied = !hasNewUserJourneyVariant && !hasIban && hasAtLeastOneDossier;

  return isNewUserJourneyDenied
    ? of({ value: abTestDefaultVariantMapping[AbTestCampaignNameEnum.NewUserJourney] })
    : getAbTest(userId, abTestWebservice, AbTestCampaignNameEnum.NewUserJourney);
};

const getAbTestCampaignByName = (name: AbTestCampaignNameEnum): { name: string; campaignId: string } | undefined => {
  return environment.abTest[environment.i18n.locale]?.find((abtest) => abtest.name === name);
};

const getAbTest = (
  userId: string,
  abTestWebservice: AbTestWebservice,
  abTestCampaignName: AbTestCampaignNameEnum
): Observable<{ value: string }> => {
  const abTest = getAbTestCampaignByName(abTestCampaignName);

  if (!abTest) {
    return of({ value: abTestDefaultVariantMapping[abTestCampaignName] });
  }

  return abTestWebservice.getVersionV2(abTest.campaignId, environment.i18n.country, UserTypeEnum.Member, userId).pipe(
    catchError((err: HttpErrorResponse) => {
      Bugsnag.notify(
        new Error(
          `Please fix the ${abTestCampaignName} ab test campaign following error: ${err.status} - ${err.error.detail}. The default value will be used`
        ),
        (event) => {
          event.severity = 'info';
        }
      );

      return of({ value: abTestDefaultVariantMapping[abTestCampaignName] });
    })
  );
};
